import React, { useState, useEffect } from 'react';
import { createAugmentationRule, updateAugmentationRule, getAugmentationRule, validateAugmentationRule } from '../services/api/augmentationRuleApi';
import { getRulesetTypesByCustomer } from '../services/api/rulesetApi';
import { validateInput, validateObject } from '../utils/sanitizer';
import { useAvailableFields } from '../hooks/useAvailableFields';
import StringManipulationRuleForm from './StringManipulationRuleForm';
import ConditionalRuleForm from './ConditionalRuleForm';
import LookupRuleForm from './LookupRuleForm';

const RuleFormDialog = ({ ruleId, customerId, onSave, onCancel, allRules }) => {
  // Compute default execution order for new rules (next available)
  const defaultExecutionOrder = !ruleId && allRules && allRules.length > 0
    ? Math.max(...allRules.map(r => r.executionOrder || 0)) + 1
    : 1;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ruleType: 'STRING_MANIPULATION',
    executionOrder: defaultExecutionOrder,
    isActive: true,
    extractionRulesetTypeId: null,
    configurationJson: {}
  });
  const [rulesetTypes, setRulesetTypes] = useState([]);
  const [loading, setLoading] = useState(!!ruleId);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [validationWarnings, setValidationWarnings] = useState([]);

  const ruleTypes = [
    { value: 'STRING_MANIPULATION', label: 'String Manipulation' },
    { value: 'CONDITIONAL', label: 'Conditional Logic' },
    { value: 'LOOKUP', label: 'Reference Data Lookup' }
  ];

  const { availableFields } = useAvailableFields(
    formData.extractionRulesetTypeId,
    formData.executionOrder,
    allRules || [],
    ruleId
  );

  useEffect(() => {
    if (customerId) {
      fetchRulesetTypes();
    }
    
    if (ruleId) {
      fetchRule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleId, customerId]);

  const fetchRulesetTypes = async () => {
    try {
      const response = await getRulesetTypesByCustomer(customerId);
      setRulesetTypes(response.data);
    } catch (err) {
      console.error('Failed to fetch ruleset types:', err);
      setRulesetTypes([]);
    }
  };

  const fetchRule = async () => {
    try {
      setLoading(true);
      const response = await getAugmentationRule(ruleId);
      const rule = response.data;
      
      setFormData({
        name: rule.name || '',
        description: rule.description || '',
        ruleType: rule.ruleType || 'STRING_MANIPULATION',
        executionOrder: rule.executionOrder || 1,
        isActive: rule.isActive !== undefined ? rule.isActive : true,
        extractionRulesetTypeId: rule.extractionRulesetTypeId || null,
        configurationJson: typeof rule.configurationJson === 'string' 
          ? JSON.parse(rule.configurationJson) 
          : rule.configurationJson || {}
      });

      // Call validate endpoint after loading rule successfully
      try {
        const validateResponse = await validateAugmentationRule(ruleId);
        if (validateResponse.data?.warnings && validateResponse.data.warnings.length > 0) {
          setValidationWarnings(validateResponse.data.warnings);
        }
      } catch (validateErr) {
        // Non-critical check — log and don't block the user
        console.error('Failed to validate rule:', validateErr);
      }
    } catch (err) {
      setError('Failed to load rule details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Validate input but don't modify it
    if (type !== 'checkbox' && !validateInput(value)) {
      setError('Input contains potentially unsafe content.');
      return;
    }
    
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleRuleTypeChange = (e) => {
    const newRuleType = e.target.value;
    setFormData(prev => ({
      ...prev,
      ruleType: newRuleType,
      configurationJson: {} // Reset configuration when changing rule type
    }));
  };

  const handleConfigurationChange = (config) => {
    setFormData(prev => ({
      ...prev,
      configurationJson: config
    }));
    // Clear warnings when config changes — they'll be re-validated on next save
    if (validationWarnings.length > 0) {
      setValidationWarnings([]);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (formData.executionOrder < 1) {
      errors.executionOrder = 'Execution order must be at least 1';
    }
    
    // Validate configuration based on rule type
    if (formData.ruleType === 'STRING_MANIPULATION') {
      if (!formData.configurationJson.sourceField) {
        errors.sourceField = 'Source field is required';
      }
      if (!formData.configurationJson.targetField) {
        errors.targetField = 'Target field is required';
      }
      if (!formData.configurationJson.operation) {
        errors.operation = 'Operation is required';
      }
    } else if (formData.ruleType === 'CONDITIONAL') {
      if (!formData.configurationJson.targetField) {
        errors.targetField = 'Target field is required';
      }
      if (!formData.configurationJson.conditions || formData.configurationJson.conditions.length === 0) {
        errors.conditions = 'At least one condition is required';
      }
    } else if (formData.ruleType === 'LOOKUP') {
      if (!formData.configurationJson.referenceDataSetId) {
        errors.referenceDataSetId = 'Reference data set is required';
      }
      if (!formData.configurationJson.referenceDataType) {
        errors.referenceDataType = 'Reference data type is required';
      }
      if (!formData.configurationJson.matchFields || formData.configurationJson.matchFields.length === 0) {
        errors.matchFields = 'At least one match field is required';
      }
      if (!formData.configurationJson.returnFields || formData.configurationJson.returnFields.length === 0) {
        errors.returnFields = 'At least one return field is required';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    setValidationErrors({});

    // Validate form
    if (!validateForm()) {
      setError('Please fix the validation errors before submitting.');
      setLoading(false);
      return;
    }

    // Validate input before submission
    const validation = validateObject({
      name: formData.name,
      description: formData.description
    });
    if (!validation.isValid) {
      setError('Input contains potentially unsafe content.');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        customerId,
        configurationJson: JSON.stringify(formData.configurationJson)
      };

      let response;
      if (ruleId) {
        response = await updateAugmentationRule(ruleId, payload);
      } else {
        response = await createAugmentationRule(payload);
      }

      setSuccess(ruleId ? 'Rule updated successfully!' : 'Rule created successfully!');
      setTimeout(() => {
        onSave();
      }, 1500);
    } catch (err) {
      if (err.response?.status === 400 && Array.isArray(err.response?.data?.errors)) {
        // Map backend validation errors to per-field errors
        const serverErrors = err.response.data.errors;
        const fieldErrors = {};
        const unmappedErrors = [];

        serverErrors.forEach(error => {
          const path = error?.path ?? '';
          const message = error?.message ?? 'Validation error';
          const rootKey = path.split('.')[0].split('[')[0];
          if (['sourceField', 'targetField', 'operation', 'conditions', 
               'referenceDataSetId', 'referenceDataType', 'matchFields', 
               'returnFields', 'parameters'].includes(rootKey)) {
            // Collect errors per root key (use first error for display)
            if (!fieldErrors[rootKey]) {
              fieldErrors[rootKey] = message;
            }
          } else {
            unmappedErrors.push(message);
          }
        });

        setValidationErrors(fieldErrors);
        const errorSummary = `${serverErrors.length} validation error(s)`;
        setError(unmappedErrors.length > 0 
          ? `${errorSummary}. ${unmappedErrors.join('; ')}` 
          : errorSummary);
      } else if (err.response?.status === 400) {
        // Handle ASP.NET model binding validation (ProblemDetails format)
        const data = err.response.data;
        if (data?.errors && typeof data.errors === 'object') {
          const messages = Object.values(data.errors).flat();
          setError(messages.join('; ') || 'Validation failed. Please check your input.');
        } else {
          setError(data?.title || data?.message || 'Validation failed. Please check your input.');
        }
      } else {
        // Existing error handling for non-validation errors
        let errorMessage = 'Rule save failed. Check your input or try again later.';
        if (err.response?.status === 500) {
          errorMessage = 'Error occurred while saving rule. Please contact support if the problem persists.';
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        }
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderConfigurationForm = () => {
    // Map warnings array to a field-keyed object for child forms
    const fieldWarnings = {};
    validationWarnings.forEach(warning => {
      const rootKey = warning.path.split('.')[0].split('[')[0];
      if (!fieldWarnings[rootKey]) {
        fieldWarnings[rootKey] = warning.message;
      }
    });

    switch (formData.ruleType) {
      case 'STRING_MANIPULATION':
        return (
          <StringManipulationRuleForm
            configuration={formData.configurationJson}
            onChange={handleConfigurationChange}
            errors={validationErrors}
            warnings={fieldWarnings}
            availableFields={availableFields}
          />
        );
      case 'CONDITIONAL':
        return (
          <ConditionalRuleForm
            configuration={formData.configurationJson}
            onChange={handleConfigurationChange}
            errors={validationErrors}
            warnings={fieldWarnings}
            availableFields={availableFields}
          />
        );
      case 'LOOKUP':
        return (
          <LookupRuleForm
            configuration={formData.configurationJson}
            onChange={handleConfigurationChange}
            customerId={customerId}
            errors={validationErrors}
            warnings={fieldWarnings}
            availableFields={availableFields}
          />
        );
      default:
        return null;
    }
  };

  if (loading && ruleId) {
    return (
      <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-body text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading rule details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{ruleId ? 'Edit Rule' : 'Create Rule'}</h5>
              <button type="button" className="btn-close" onClick={onCancel} disabled={loading}></button>
            </div>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              
              {success && (
                <div className="alert alert-success" role="alert">
                  {success}
                </div>
              )}

              {validationWarnings.length > 0 && (
                <div className="alert alert-warning" role="alert">
                  <strong>Warnings:</strong>
                  <ul className="mb-0 mt-1">
                    {validationWarnings.map((warning, idx) => (
                      <li key={idx}>{warning.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                {/* Common Fields */}
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className={`form-control ${validationErrors.name ? 'is-invalid' : ''}`}
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                  {validationErrors.name && (
                    <div className="invalid-feedback">{validationErrors.name}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="description" className="form-label">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    className="form-control"
                    value={formData.description}
                    onChange={handleChange}
                    rows={2}
                    disabled={loading}
                  />
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="ruleType" className="form-label">
                      Rule Type <span className="text-danger">*</span>
                    </label>
                    <select
                      id="ruleType"
                      name="ruleType"
                      className="form-select"
                      value={formData.ruleType}
                      onChange={handleRuleTypeChange}
                      disabled={loading || ruleId} // Can't change type when editing
                      required
                    >
                      {ruleTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="executionOrder" className="form-label">
                      Execution Order <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      id="executionOrder"
                      name="executionOrder"
                      className={`form-control ${validationErrors.executionOrder ? 'is-invalid' : ''}`}
                      value={formData.executionOrder}
                      onChange={handleChange}
                      min="1"
                      required
                      disabled={loading}
                    />
                    {validationErrors.executionOrder && (
                      <div className="invalid-feedback">{validationErrors.executionOrder}</div>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="extractionRulesetTypeId" className="form-label">
                    Ruleset Type (Optional)
                  </label>
                  <select
                    id="extractionRulesetTypeId"
                    name="extractionRulesetTypeId"
                    className="form-select"
                    value={formData.extractionRulesetTypeId || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      extractionRulesetTypeId: e.target.value ? parseInt(e.target.value) : null
                    }))}
                    disabled={loading}
                  >
                    <option value="">All Ruleset Types</option>
                    {rulesetTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted">
                    Leave empty to apply this rule to all ruleset types
                  </small>
                </div>

                <div className="mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="isActive"
                      name="isActive"
                      className="form-check-input"
                      checked={formData.isActive}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    <label htmlFor="isActive" className="form-check-label">
                      Active
                    </label>
                  </div>
                </div>

                <hr />

                {/* Rule-Specific Configuration */}
                <h6 className="mb-3">Rule Configuration</h6>
                {Object.keys(validationErrors).some(k => 
                  ['sourceField', 'targetField', 'operation', 'conditions', 
                   'referenceDataSetId', 'referenceDataType', 'matchFields', 
                   'returnFields', 'parameters'].includes(k)
                ) && (
                  <div className="alert alert-danger" role="alert">
                    {Object.entries(validationErrors)
                      .filter(([k]) => ['sourceField', 'targetField', 'operation', 'conditions', 
                        'referenceDataSetId', 'referenceDataType', 'matchFields', 
                        'returnFields', 'parameters'].includes(k))
                      .map(([, v]) => v)
                      .join('; ')}
                  </div>
                )}
                {renderConfigurationForm()}

                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={onCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : (ruleId ? 'Update Rule' : 'Create Rule')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

export default RuleFormDialog;
